/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from '../../../../core';
import { mergeHeaders, mergeOnlyDefinedHeaders } from '../../../../core/headers';
import * as environments from '../../../../environments';
import * as errors from '../../../../errors/index';
import * as serializers from '../../../../serialization/index';
import * as Truvity from '../../../index';

export declare namespace Keys {
    export interface Options {
        environment?: core.Supplier<environments.TruvityEnvironment | string>;
        /** Specify a custom URL to connect the client to. */
        baseUrl?: core.Supplier<string>;
        apiKey?: core.Supplier<string | undefined>;
        /** Additional headers to include in requests. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
        fetcher?: core.FetchFunction;
    }

    export interface RequestOptions {
        /** The maximum time to wait for a response in seconds. */
        timeoutInSeconds?: number;
        /** The number of times to retry the request. Defaults to 2. */
        maxRetries?: number;
        /** A hook to abort the request. */
        abortSignal?: AbortSignal;
        /** Additional query string parameters to include in the request. */
        queryParams?: Record<string, unknown>;
        /** Additional headers to include in the request. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
    }
}

/**
 * The API to manage cryptographical asymmetrical keys.
 */
export class Keys {
    protected readonly _options: Keys.Options;

    constructor(_options: Keys.Options = {}) {
        this._options = _options;
    }

    /**
     * Generate a new cryptographic key pair.
     *
     * @param {Truvity.KeyGenerateRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyGenerate({
     *         data: {
     *             type: "ED25519"
     *         }
     *     })
     */
    public keyGenerate(
        request: Truvity.KeyGenerateRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyGenerate(request, requestOptions));
    }

    private async __keyGenerate(
        request: Truvity.KeyGenerateRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceKeyPublic>> {
        const { idempotencyKey, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                'keys/generate',
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.KeyGenerateRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /keys/generate.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * You could use KeyGenerate, if you want to rely to generated by Truvity key-pairs for CredentialIssue and PresentationIssue.
     * Remember, that you do not have any option to get private key by Truvity API, you could only use it
     *
     * If you prefer to keep private key, then you need to generate it manually.
     * To use manually generated key CredentialIssue and PresentationIssue you need to import it.
     *
     * To generate key-pair you have two options
     * 1. OpenSSL - [OpenSSL commands](https://www.openssl.org/docs/man1.1.1/man1/index.html)
     * 2. JWX tool - [https://github.com/lestrrat-go/jwx/blob/main/cmd/jwx/README.md](https://github.com/lestrrat-go/jwx/blob/main/cmd/jwx/README.md)
     *
     * How to install JWX
     * 1. Download & Install Golang - [https://go.dev/doc/install](https://go.dev/doc/install)
     * 2. Clone & Build & Install [github.com/lestrrat-go/jwx](https://github.com/lestrrat-go/jwx)
     * 	```bash
     * 	git clone https://github.com/lestrrat-go/jwx
     * 	cd jwx/cmd/jwx
     * 	go install -tags=jwx_es256k,jwx_secp256k1_pem .
     * 	```
     * 3. Verify that you correctly install everything
     * 	```bash
     * 	➜ jwx --help
     * 	NAME:
     * 		jwx - Tools for various JWE/JWK/JWS/JWT operations
     *
     * 	 USAGE:
     * 		jwx [global options] command [command options] [arguments...]
     *
     * 	 COMMANDS:
     * 		jwa      List available algorithms and types
     * 		jwe      Work with JWE messages
     * 		jwk      Work with JWK and JWK sets
     * 		jws      Work with JWS messages
     * 		help, h  Shows a list of commands or help for one command
     *
     * 	 GLOBAL OPTIONS:
     * 		--help, -h  show help
     * 	```
     *
     * How generate private key by `jwx` (output format JWK)
     * ```bash
     * # ED25519
     * jwx jwx generate --type OKP --curve Ed25519
     * # P256
     * jwx jwx generate --type EC --curve P-256
     * # P384
     * jwx jwx generate --type EC --curve P-384
     * ```
     * How to generate public key from private key by `jwx` (input format - JWK, output format - JWK)
     * ```bash
     * jwx jwk format -I json -O json - <(pathfile with private key in JWK format)
     * ```
     * How to generate private key by OpenSSL (output format PEM - PKCS#8)
     * ```bash
     * # ED25519
     * openssl genpkey -algorithm ed25519 | openssl pkcs8 -nocrypt -topk8
     * # P256
     * openssl ecparam -name prime256v1 -noout -genkey | openssl pkcs8 -nocrypt -topk8
     * # P384
     * openssl ecparam -name secp384r1 -noout -genkey | openssl pkcs8 -nocrypt -topk8
     * ```
     * How to transform existing private key to PKCS8 format by OpenSSL (input format PEM, output format - PEM PCKS#8)
     * ```bash
     * openssl pkcs8 -nocrypt -topk8 <(pathfile with private key in PEM format)
     * ```
     * How to transform private key to public key by OpenSSL (input format PEM, output format - PEM PKIX)
     * ```bash
     * openssl pkey -pubout <(pathfile with private key in PKCS8 format)
     * ```
     * Useful third-party documentation:
     * - [openssl-genrsa - generate an RSA private key](https://www.openssl.org/docs/manmaster/man1/openssl-genpkey.html).
     * - [openssl-ec, ec - EC key processing](https://www.openssl.org/docs/man1.0.2/man1/ec.html)
     * - [openssl-genrsa - generate an RSA private key](https://www.openssl.org/docs/man3.0/man1/openssl-genrsa.html)
     * - [openssl-pkcs8 - PKCS#8 format private key conversion command](https://www.openssl.org/docs/manmaster/man1/openssl-pkcs8.html)
     * - [JWX - github.com/lestrrat-go/jwx](https://github.com/lestrrat-go/jwx/blob/main/cmd/jwx/README.md)
     * - [FIPS 186-4](https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.186-4.pdf).
     * - [SEC2](https://www.secg.org/sec2-v2.pdf).
     * - [AWS KMS - Asymmetric key specs](https://docs.aws.amazon.com/kms/latest/developerguide/asymmetric-key-specs.html)
     * .
     *
     * @param {Truvity.KeyImportRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyImport({
     *         data: "data"
     *     })
     */
    public keyImport(
        request: Truvity.KeyImportRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyImport(request, requestOptions));
    }

    private async __keyImport(
        request: Truvity.KeyImportRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceKeyPublic>> {
        const { idempotencyKey, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                'keys/import',
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.KeyImportRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /keys/import.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Search for Keys with extended filters capabilities.
     *
     * @param {Truvity.KeySearchRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keySearch()
     */
    public keySearch(
        request: Truvity.KeySearchRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ListKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keySearch(request, requestOptions));
    }

    private async __keySearch(
        request: Truvity.KeySearchRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ListKeyPublic>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                'keys/search',
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.KeySearchRequest.jsonOrThrow(request, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ListKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /keys/search.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Get the latest version of Key.
     *
     * @param {string} id - The ID of the resource.
     * @param {Truvity.KeyLatestRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyLatest("id")
     */
    public keyLatest(
        id: string,
        request: Truvity.KeyLatestRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyLatest(id, request, requestOptions));
    }

    private async __keyLatest(
        id: string,
        request: Truvity.KeyLatestRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceKeyPublic>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling GET /keys/{id}.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Delete Key's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.KeyDeleteRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyDelete("id", {
     *         ifMatch: "If-Match"
     *     })
     */
    public keyDelete(
        id: string,
        request: Truvity.KeyDeleteRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<void> {
        return core.HttpResponsePromise.fromPromise(this.__keyDelete(id, request, requestOptions));
    }

    private async __keyDelete(
        id: string,
        request: Truvity.KeyDeleteRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<void>> {
        const { idempotencyKey, ifMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}`,
            ),
            method: 'DELETE',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: undefined, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling DELETE /keys/{id}.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Update annotations of Key's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.KeyUpdateAnnotationsRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyUpdateAnnotations("id", {
     *         ifMatch: "If-Match",
     *         annotations: {
     *             "key": "value"
     *         }
     *     })
     */
    public keyUpdateAnnotations(
        id: string,
        request: Truvity.KeyUpdateAnnotationsRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyUpdateAnnotations(id, request, requestOptions));
    }

    private async __keyUpdateAnnotations(
        id: string,
        request: Truvity.KeyUpdateAnnotationsRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceKeyPublic>> {
        const { idempotencyKey, ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/annotations`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.KeyUpdateAnnotationsRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling PATCH /keys/{id}/annotations.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Sign a JSON-LD representation of an unsigned Credential.
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     */
    public credentialSign(
        id: string,
        request: Truvity.CredentialSignRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<core.BinaryResponse> {
        return core.HttpResponsePromise.fromPromise(this.__credentialSign(id, request, requestOptions));
    }

    private async __credentialSign(
        id: string,
        request: Truvity.CredentialSignRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<core.BinaryResponse>> {
        const { didMethod, body: _body } = request;
        const _queryParams: Record<string, string | string[] | object | object[] | null> = {};
        if (didMethod != null) {
            _queryParams['did_method'] = serializers.CredentialSignRequestDidMethod.jsonOrThrow(didMethod, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            });
        }

        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)<core.BinaryResponse>({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/credentials/sign`,
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            requestType: 'json',
            body: serializers.keys.credentialSign.Request.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            responseType: 'binary-response',
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /keys/{id}/credentials/sign.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Sign DIDComm Message Plaintext.
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     */
    public didCommMessageSign(
        id: string,
        request: Truvity.DidCommMessageSignRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<core.BinaryResponse> {
        return core.HttpResponsePromise.fromPromise(this.__didCommMessageSign(id, request, requestOptions));
    }

    private async __didCommMessageSign(
        id: string,
        request: Truvity.DidCommMessageSignRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<core.BinaryResponse>> {
        const { didMethod, body: _body } = request;
        const _queryParams: Record<string, string | string[] | object | object[] | null> = {};
        if (didMethod != null) {
            _queryParams['did_method'] = serializers.DidCommMessageSignRequestDidMethod.jsonOrThrow(didMethod, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            });
        }

        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)<core.BinaryResponse>({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/didcomm-messages/sign`,
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            requestType: 'json',
            body: serializers.keys.didCommMessageSign.Request.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            responseType: 'binary-response',
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling POST /keys/{id}/didcomm-messages/sign.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Update labels of Key's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.KeyUpdateLabelsRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyUpdateLabels("id", {
     *         ifMatch: "If-Match",
     *         labels: {
     *             "key": "value"
     *         }
     *     })
     */
    public keyUpdateLabels(
        id: string,
        request: Truvity.KeyUpdateLabelsRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyUpdateLabels(id, request, requestOptions));
    }

    private async __keyUpdateLabels(
        id: string,
        request: Truvity.KeyUpdateLabelsRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceKeyPublic>> {
        const { idempotencyKey, ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/labels`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.KeyUpdateLabelsRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling PATCH /keys/{id}/labels.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Sign a JSON-LD representation of an unsigned Presentation.
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     */
    public presentationSign(
        id: string,
        request: Truvity.PresentationSignRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<core.BinaryResponse> {
        return core.HttpResponsePromise.fromPromise(this.__presentationSign(id, request, requestOptions));
    }

    private async __presentationSign(
        id: string,
        request: Truvity.PresentationSignRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<core.BinaryResponse>> {
        const { didMethod, body: _body } = request;
        const _queryParams: Record<string, string | string[] | object | object[] | null> = {};
        if (didMethod != null) {
            _queryParams['did_method'] = serializers.PresentationSignRequestDidMethod.jsonOrThrow(didMethod, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            });
        }

        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)<core.BinaryResponse>({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/presentations/sign`,
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            requestType: 'json',
            body: serializers.keys.presentationSign.Request.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            responseType: 'binary-response',
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling POST /keys/{id}/presentations/sign.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Restore Key's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.KeyRestoreRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyRestore("id", {
     *         ifMatch: "If-Match"
     *     })
     */
    public keyRestore(
        id: string,
        request: Truvity.KeyRestoreRequest,
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyRestore(id, request, requestOptions));
    }

    private async __keyRestore(
        id: string,
        request: Truvity.KeyRestoreRequest,
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceKeyPublic>> {
        const { idempotencyKey, ifMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/restore`,
            ),
            method: 'POST',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /keys/{id}/restore.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * History of the Key's resources.
     *
     * @param {string} id - The ID of the resource.
     * @param {Truvity.KeyHistoryRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyHistory("id")
     */
    public keyHistory(
        id: string,
        request: Truvity.KeyHistoryRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ListKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyHistory(id, request, requestOptions));
    }

    private async __keyHistory(
        id: string,
        request: Truvity.KeyHistoryRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ListKeyPublic>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/revisions`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ListKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling GET /keys/{id}/revisions.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Read the specific version of the Key's resource.
     *
     * @param {string} id - The ID of the resource.
     * @param {number} revision - The revision of the resource.
     * @param {Truvity.KeyRevisionRequest} request
     * @param {Keys.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.keys.keyRevision("id", 1)
     */
    public keyRevision(
        id: string,
        revision: number,
        request: Truvity.KeyRevisionRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceKeyPublic> {
        return core.HttpResponsePromise.fromPromise(this.__keyRevision(id, revision, request, requestOptions));
    }

    private async __keyRevision(
        id: string,
        revision: number,
        request: Truvity.KeyRevisionRequest = {},
        requestOptions?: Keys.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceKeyPublic>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `keys/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revision)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceKeyPublic.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling GET /keys/{id}/revisions/{revision}.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    protected async _getCustomAuthorizationHeaders() {
        const apiKeyValue = await core.Supplier.get(this._options.apiKey);
        return { 'X-API-KEY': apiKeyValue };
    }
}
